import { useCallback, useMemo, useRef } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Pager from './Pager';
import ProductSummaryBand from './ProductSummaryBand';
import RatingSummary from './RatingSummary';
import ReviewList from './ReviewList';
import Tabs from './Tabs';
import Toolbar from './Toolbar';
import useReviews from './useReviews';
import { normalizeProductSummary, normalizeReviewPage, normalizeSummary } from './api';
import { ApiConfig, Faq, Payloads, ReviewSource, SortOption, Tab } from './types';

export type Props = {
  api: ApiConfig;
  /** Which tab the server fetched, and so which one this first paint is showing. */
  tab: Tab;
  payloads: Payloads;
  sortOptions: SortOption[];
  source: ReviewSource;
  faqs: Faq[];
};

const STATS = [
  { value: '1,000,000+', label: 'magazines & books' },
  { value: 'No', label: 'hidden fees' },
  { value: 'Free', label: 'shipping for magazines' },
  { value: '100%', label: 'satisfaction guaranteed' },
];

/** Breathing room between the site's fixed header and the heading we scroll up to. */
const SCROLL_GAP_PX = 12;

/**
 * The two tabs, and the only two urls this page has. Everything else a visitor can vary
 * is a request rather than an address.
 */
const TABS: { key: Tab; label: string; url: string }[] = [
  { key: 'seller', label: 'Seller reviews', url: '/reviews' },
  { key: 'products', label: 'Product reviews', url: '/reviews?tab=products' },
];

/**
 * /reviews, driven end to end by SmartCustomer's public v2 API.
 *
 * There is no store behind this page and no sync job feeding one. Everything a visitor
 * does — turning a page, sorting, filtering, searching, switching tabs — is a request
 * the browser makes directly to SmartCustomer, and the list updates in place rather than
 * reloading the page, under one loading indicator drawn over the reviews themselves.
 *
 * What the server still does, and what must not be lost: it fetches page one of the
 * requested tab and the summary figures, so the review text and the AggregateRating are
 * in the raw HTML. This page ranks top 3 for "discountmags reviews" and the widget it
 * replaces put no review text in the source at all — a first paint that is only a
 * loading state would hand that back. So the hero, the rating card and the first fifteen
 * reviews are real markup; the loading states below exist for the second page onwards.
 *
 * Nothing here varies per visitor, so both the props and the render are cached. Keep it
 * that way: a cart, a name or an offer code anywhere in here would be served to every
 * visitor from one cache entry.
 */
export default function Reviews({ api, tab, payloads, sortOptions, source, faqs }: Props) {
  const { buildUrl } = useUrlGenerator();

  // Read here rather than on the server, by the same functions that read every page the
  // browser fetches later. The props builder hands these over exactly as SmartCustomer
  // answered them.
  const summary = useMemo(() => normalizeSummary(payloads.summary, payloads.seller), [payloads]);

  const productSummary = useMemo(
    () => normalizeProductSummary(payloads.summary, payloads.products),
    [payloads]
  );

  const initial = useMemo(
    () => normalizeReviewPage(tab === 'products' ? payloads.products : payloads.seller, tab),
    [payloads, tab]
  );

  const tabs = useMemo(
    () =>
      TABS.map((descriptor) => ({
        ...descriptor,
        count: descriptor.key === 'products' ? productSummary.reviewCount : summary.reviewCount,
      })),
    [summary.reviewCount, productSummary.reviewCount]
  );

  const reviews = useReviews({
    api,
    sortOptions,
    initial,
    // Derived from the same urls the tab anchors use, so what a visitor copies out of the
    // address bar is always the address the link would have taken them to.
    tabUrls: Object.fromEntries(
      tabs.map((descriptor) => [descriptor.key, buildUrl(descriptor.url)])
    ) as Record<Tab, string>,
  });

  const isProductTab = reviews.view.tab === 'products';

  const headingRef = useRef<HTMLHeadingElement>(null);

  /**
   * Turning a page takes the visitor back to the top of the reviews.
   *
   * The pager sits under fifteen cards, so a new page arrives entirely above the fold and
   * the click reads as having done nothing. Scrolling happens on the click rather than
   * when the reviews land, so what travels up the screen is the section already showing
   * that it is fetching.
   */
  const goToPage = useCallback(
    (page: number) => {
      reviews.goToPage(page);

      const heading = headingRef.current;

      if (!heading) {
        return;
      }

      // The site header is fixed, so the top of the viewport is behind it. Measured on the
      // click, because it is one height on a phone, another on a desktop, and shorter
      // again once the page has been scrolled.
      const header = document.querySelector('header');
      const covered = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;
      const top = heading.getBoundingClientRect().top + window.scrollY - covered - SCROLL_GAP_PX;

      // Upwards only: on a short last page the heading can already be on screen, and
      // dragging the page down to line it up exactly is the opposite of what was asked.
      if (window.scrollY > top) {
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },
    [reviews.goToPage]
  );

  return (
    <div className="text-slate-800">
      {/* The page's own trail, not the layout's. */}
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Customer Reviews' }]} />

      <PageHeading
        hero
        eyebrow="Verified customer reviews"
        intro={
          <>
            As a family owned small business operating since 2002, we take pride in providing
            readers with the guaranteed lowest prices on over 1,000,000 magazine subscriptions
            &amp; books — with no hidden fees.
            {` Every review below is independently collected and verified by ${source.name} from confirmed customers.`}
          </>
        }
      >
        DiscountMags Customer Reviews
      </PageHeading>

      <RatingSummary summary={summary} source={source} />

      <ul
        className={cn(
          // Inset to line up with the rating card above it, which it reads as a pair with.
          'mx-4 grid list-none grid-cols-2 rounded-xl border border-solid border-slate-200 bg-white p-0',
          'md:mx-7 md:grid-cols-4',
          // The rating card pulls itself up over the hero; with no reviews to show it is
          // not rendered at all, and this would otherwise sit under the overlap.
          summary.reviewCount > 0 ? 'mt-6' : 'mt-8'
        )}
      >
        {STATS.map((stat, index) => (
          <li
            key={stat.label}
            className={cn(
              'px-2 py-3.5 text-center text-xs text-slate-500',
              'border-0 border-solid border-slate-200 md:border-t-0',
              // Dividers between cells: two columns on a phone, four above it.
              index % 2 === 1 && 'border-l',
              index >= 2 && 'border-t',
              index > 0 && 'md:border-l'
            )}
          >
            <b className="block text-base font-bold text-slate-900">{stat.value}</b>
            {stat.label}
          </li>
        ))}
      </ul>

      <section aria-labelledby="recent-reviews" className="mt-9">
        <h2 ref={headingRef} id="recent-reviews" className="m-0 mb-4 text-xl font-bold tracking-tight">
          {isProductTab ? 'Recent product reviews' : 'Recent reviews'}
        </h2>

        <div className="mb-4">
          <Tabs tabs={tabs} current={reviews.view.tab} onSelect={reviews.goToTab} buildUrl={buildUrl} />
        </div>

        {isProductTab && <ProductSummaryBand summary={productSummary} />}

        <Toolbar
          tab={reviews.view.tab}
          search={reviews.searchInput}
          onSearchChange={reviews.setSearchInput}
          ratings={reviews.view.ratings}
          onToggleRating={reviews.toggleRating}
          onClearRatings={reviews.clearRatings}
          sortOptions={sortOptions}
          sort={reviews.view.sort}
          onSortChange={reviews.changeSort}
          isFiltered={reviews.isFiltered}
          onClear={reviews.clearFilters}
        />

        <ReviewList
          tab={reviews.view.tab}
          page={reviews.page}
          isLoading={reviews.isLoading}
          hasFailed={reviews.hasFailed}
          isFiltered={reviews.isFiltered}
          perPage={api.perPage}
          source={source}
          onClearFilters={reviews.clearFilters}
        />

        <Pager
          currentPage={reviews.view.page}
          totalPages={reviews.totalPages}
          onSelect={goToPage}
          isLoading={reviews.isLoading}
        />

        <p className="mt-4 text-center text-xs text-slate-500">
          Reviews independently collected &amp; verified by{' '}
          <a href={source.url} target="_blank" rel="noopener nofollow" className="underline">
            {source.name}
          </a>
        </p>
      </section>

      <section aria-labelledby="reviews-faq" className="mt-12">
        <h2 id="reviews-faq" className="m-0 text-xl font-bold tracking-tight">
          Frequently asked questions
        </h2>
        <p className="mb-4 mt-1 text-sm text-slate-500">
          Straight answers for shoppers checking us out before ordering.
        </p>

        <div className="grid gap-2.5">
          {faqs.map((faq, index) => (
            <FaqAccordion
              key={faq.question}
              question={faq.question}
              defaultOpen={index === 0}
              answer={
                // Our own copy, built alongside the FAQPage schema so the two say the
                // same thing. It carries links, which is the only reason it is html.
                <span dangerouslySetInnerHTML={{ __html: faq.answer }} />
              }
            />
          ))}
        </div>
      </section>

      <section
        className={cn(
          'mt-12 flex flex-wrap items-center justify-between gap-6 rounded-xl',
          'bg-gradient-to-br from-slate-800 to-slate-700 px-8 py-8 text-white'
        )}
      >
        <div>
          <h2 className="m-0 text-lg font-bold tracking-tight">
            {summary.reviewCount > 0
              ? `Convinced? Join ${summary.reviewCount.toLocaleString('en-US')} happy reviewers.`
              : 'Convinced? Start with our best sellers.'}
          </h2>
          <p className="m-0 mt-1 text-sm opacity-80">
            Guaranteed lowest prices on 1,000,000+ magazines &amp; books — no hidden fees, free shipping for
            magazines.
          </p>
        </div>

        {/* One button, not two: /page/browse redirects to /bestsellers, so the pair was
            the same destination twice. */}
        <a
          href={buildUrl('/bestsellers')}
          className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white no-underline hover:bg-primary-700"
        >
          Shop Best Sellers
        </a>
      </section>
    </div>
  );
}

Reviews.layoutName = 'Layouts.Institutional';
